<template>
  <div class="relative" ref="dataContainer"
  :class="{'cursor-pointer': hoveredDataPoint}"
  :style="{
    right: `${CANVAS_PADDING}px`,
    bottom: `${CANVAS_PADDING}px`,
    width: `calc(100% + ${CANVAS_PADDING * 2}px)`,
    height: `calc(100% + ${CANVAS_PADDING * 2}px)`
  }"
  @mousemove="handleMouseMove" @mouseleave="hoveredDataPoint = null, previewData = null"
  @click="handlePreviewClick"
  >
    <canvas ref="dataCanvas" />
    <transition name="fade">
      <div v-if="previewData" class="asset-preview-container anim-transform" :style="{ transform: tooltipTransform }">
        <BasePopupVideoPreview 
          v-if="previewData.type === 'video'"
          ref="previewVideoPlayer"
          class="relative"
          :title="previewData?.adName"
          :video-src="previewData.src"
          :attempt-manual-play="attemptManualPreviewPlayCounter"
        />
        <BasePopupThumbnailPreview
          v-else
          class="relative"
          :title="previewData?.adName"
          :thumbnail-src="previewData.src"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { quadtree } from 'd3-quadtree'

const DATA_POINT_RADIUS = 3 // px
const CANVAS_PADDING = DATA_POINT_RADIUS + 10 // px
const DATA_POINT_HOVER_THRESHOLD = 10 // px

const MAX_COMPUTE_RETRIES = 3

export default {
  name: 'DataRenderer',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    axisScales: {
      type: Array,
      default: () => []
    },
    performanceKpi: {
      type: String,
      default: 'target_metric_value'
    },
    scaleKpi: {
      type: String,
      default: 'spend'
    },
    dataAreaDimensions: {
      type: Object,
      default: () => {}
    },
    isActiveTab: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      dataPoints: [],
      hoveredDataPoint: null,
      previewData: null,
      pointQuadTree: null,
      computeRetries: 0,
      attemptManualPreviewPlayCounter: 0,
      browserDPR: 1,
      mainLayoutHasScrollbar: false,
      mainLayoutObserver: null,
      CANVAS_PADDING,
      DATA_POINT_RADIUS
    }
  },
  mounted () {
    this.$nextTick(() => { this.computeDataPoints() })
    
    // Define handlers for browser zoom (recompute if browser zoom changes)
    if (window.visualViewport) {
      this.browserDPR = window.devicePixelRatio
      window.visualViewport.addEventListener('resize', this.handleBrowserZoom)
    }

    // Define handler for main layout scrollbar appearance (recompute if scrollbar appears/disappears)
    const mainLayout = document.querySelector('.main-layout')
    this.handleMainLayoutScrollbarAppearance(mainLayout)
    if (mainLayout) {
      this.mainLayoutObserver = new ResizeObserver(() => {
        this.handleMainLayoutScrollbarAppearance(mainLayout)
      })
      this.mainLayoutObserver.observe(mainLayout)
    }
  },
  beforeDestroy () {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleBrowserZoom)
    }
    if (this.mainLayoutObserver) {
      this.mainLayoutObserver.disconnect()
      this.mainLayoutObserver = null
    }
  },
  watch: {
    data () {
      this.$nextTick(() => { this.computeDataPoints() })
    },
    hoveredDataPoint () {
      this.drawCanvas()
    }
  },
  computed: {
    tooltipTransform () {
      if (!this.previewData) return 'translate(0, 0)'
      const { xTransform, yTransform } = this.previewData
      const xStr = `calc(-50% + ${xTransform}px)`
      const yStr = `calc(100% - ${yTransform}px)`
      return `translate(${xStr}, ${yStr})`
    }
  },
  methods: {
    computeDataPoints () {
      if (!this.isActiveTab) return
      if (!this.axisScales?.length || !this.dataAreaDimensions?.width || !this.data?.length) {
        console.log(`Missing necessary data for chart rendering, retrying in 50ms (${this.computeRetries}/${MAX_COMPUTE_RETRIES})`)
        this.dataPoints = []
        this.pointQuadTree = null
        if (this.computeRetries < MAX_COMPUTE_RETRIES) {
          this.computeRetries++
          setTimeout(() => { this.computeDataPoints() }, 50)
        }
        return
      }
      this.computeRetries = 0
      const [ xScale, yScale ] = this.axisScales
      // We have to expand the bounds of the render area to account for points rendered on the edges
      const availableWidth = this.dataAreaDimensions.width + CANVAS_PADDING * 2
      const availableHeight = this.dataAreaDimensions.height + CANVAS_PADDING * 2
      // Adjust the scales' output ranges to account for this canvas 'padding'
      xScale.range([CANVAS_PADDING, availableWidth - CANVAS_PADDING])
      yScale.range([CANVAS_PADDING, availableHeight - CANVAS_PADDING])

      const dataPointsArr = this.data.map(ad => {
        const xValue = ad[this.performanceKpi]
        const yValue = ad[this.scaleKpi]
        const xPos = Math.round(xScale(xValue))
        const yPos = Math.round(yScale(yValue))
        return {
          data: ad,
          xPos,
          yPos
        }
      })
      this.dataPoints = dataPointsArr

      // Populate the quadtree for mouseover hit detection
      this.pointQuadTree = quadtree()
        .x(d => d.xPos)
        .y(d => d.yPos)
        .addAll(dataPointsArr)

      // --- RENDER THE DATA POINTS IN THE CANVAS ---
      this.drawCanvas()
    },
    drawCanvas () {
      // We have to expand the bounds of the render area to account for points rendered on the edges
      const availableWidth = this.dataAreaDimensions.width + CANVAS_PADDING * 2
      const availableHeight = this.dataAreaDimensions.height + CANVAS_PADDING * 2

      const canvas = this.$refs.dataCanvas
      const ctx = canvas?.getContext('2d')
      if (!canvas || !ctx) {
        console.error("Couldn't access canvas ref/context for data rendering")
        return
      }

      // Scale the canvas for high-DPI displays
      const devicePixelRatio = window.devicePixelRatio || 1
      canvas.style.width = `${availableWidth}px`
      canvas.style.height = `${availableHeight}px`
      canvas.width = availableWidth * devicePixelRatio
      canvas.height = availableHeight * devicePixelRatio
      ctx.setTransform(1, 0, 0, 1, 0, 0) // Reset the transform matrix
      ctx.scale(devicePixelRatio, devicePixelRatio) // Apply the dpi scale
      // Change coordinate origin to bottom-left
      ctx.translate(0, availableHeight)
      ctx.scale(1, -1)

      // Clear the canvas
      ctx.clearRect(0, 0, availableWidth, availableHeight)

      const drawPoint = (xPos, yPos, color) => {
        ctx.beginPath()
        ctx.lineWidth = 1
        ctx.arc(xPos, yPos, DATA_POINT_RADIUS, 0, Math.PI * 2)
        ctx.fillStyle = color
        ctx.fill()
      }
      
      // Render the data points
      const activePoints = []
      for (let i = 0; i < this.dataPoints.length; i++) {
        const { xPos, yPos, data } = this.dataPoints[i]
        if (this.hoveredDataPoint?.data?.creative_hash === data.creative_hash) continue
        if (data.ad_status === 'ACTIVE') {
          activePoints.push({ xPos, yPos, data })
          continue
        }
        drawPoint(xPos, yPos, '#C1C7D0', this.hoveredDataPoint?.data?.creative_hash === data.creative_hash)
      }
      // Render the active points (do them last so they're on top)
      for (let i = 0; i < activePoints.length; i++) {
        const { xPos, yPos, data } = activePoints[i]
        drawPoint(xPos, yPos, '#40C4AA', this.hoveredDataPoint?.data?.creative_hash === data.creative_hash)
      }
      // Render the hovered point (if it exists)
      if (this.hoveredDataPoint) {
        const { xPos, yPos, data } = this.hoveredDataPoint
        ctx.beginPath()
        ctx.arc(xPos, yPos, DATA_POINT_RADIUS + 6, 0, Math.PI * 2)
        ctx.fillStyle = '#FFFFFF'
        ctx.fill()
        ctx.beginPath()
        ctx.arc(xPos, yPos, DATA_POINT_RADIUS, 0, Math.PI * 2)
        ctx.fillStyle = data.ad_status === 'ACTIVE' ? '#40C4AA' : '#C1C7D0'
        ctx.fill()
        ctx.beginPath()
        ctx.arc(xPos, yPos, DATA_POINT_RADIUS + 6, 0, Math.PI * 2)
        ctx.strokeStyle = data.ad_status === 'ACTIVE' ? '#40C4AA' : '#C1C7D0'
        ctx.lineWidth = 2
        ctx.stroke()
      }
    },
    handleMouseMove (event) {
      if (!this.$refs.dataContainer || !this.pointQuadTree) {
        if (this.hoveredDataPoint) this.hoveredDataPoint = null
        return
      }
      const { clientX, clientY } = event
      const renderArea = this.$refs.dataContainer
      const renderAreaRect = renderArea.getBoundingClientRect()
      const mouseX = clientX - renderAreaRect.left
      const mouseY = renderArea.clientHeight - (clientY - renderAreaRect.top) // convert to canvas coordinates

      // Find the closest point to the mouse position (within a threshold)
      const nearestPoint = this.pointQuadTree.find(mouseX, mouseY, DATA_POINT_HOVER_THRESHOLD)
      this.hoveredDataPoint = nearestPoint ?? null
      this.updatePreviewData(nearestPoint)
    },
    updatePreviewData: _.debounce(function (hoveredDataPoint) {
      if (!hoveredDataPoint) {
        this.previewData = null
        return
      }
      const { xPos, yPos, data } = hoveredDataPoint

      // Extract the asset src and determine its type
      const type = data?.video_url_source?.length ? 'video' : 'image'
      const src = type === 'video' ? data?.video_url_source : data?.ad_image_url
      const adName = data?.ad_name

      // Compute the transform offset
      const xTransform = xPos
      const yTransform = yPos - 14 // 14px y-gap

      this.previewData = { src, type, adName, xTransform, yTransform }
    }, 20),
    handlePreviewClick () {
      const videoPlayer = this.$refs.previewVideoPlayer
      if (videoPlayer && videoPlayer.getIsPlaybackBlocked()) {
        this.attemptManualPreviewPlayCounter++
        return
      }
      if (this.hoveredDataPoint) {
        const { data } = this.hoveredDataPoint
        this.$emit('show-insights-drawer', data)
        this.hoveredDataPoint = null
        this.previewData = null
      }
    },
    handleBrowserZoom () {
      if (this.browserDPR !== window.devicePixelRatio) {
        this.browserDPR = window.devicePixelRatio
        setTimeout(() => { this.computeDataPoints() }, 50)
      }
    },
    handleMainLayoutScrollbarAppearance (domRef) {
      const doesOverflow = domRef.scrollHeight > domRef.clientHeight
      if (doesOverflow !== this.mainLayoutHasScrollbar) {
        this.mainLayoutHasScrollbar = doesOverflow
        setTimeout(() => { this.computeDataPoints() }, 50)
      }
    },
    resetComputeRetries () {
      this.computeRetries = 0
    }
  }
}
</script>

<style scoped>
.asset-preview-container {
  position: absolute;
  bottom: 0;
  left: 0;
  transform-origin: top center;
  z-index: 60000;
}
.asset-preview-container.anim-transform {
  transition: transform 75ms ease-in-out;
}

.fade-enter-active {
  animation: fade-in 75ms ease-in-out forwards;
}
.fade-leave-active {
  animation: fade-out 75ms ease-in-out forwards;
}
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes fade-out {
  from { opacity: 1; }
  to { opacity: 0; }
}
</style>